import { createContext, useContext, useState, useEffect } from "react";
import { translations } from "./translations";

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try {
      const urlLang = new URLSearchParams(window.location.search).get("lang");
      if (urlLang && translations[urlLang]) return urlLang;
    } catch { /* ignore */ }
    return localStorage.getItem("lockerroom_lang") || "en";
  });

  useEffect(() => {
    localStorage.setItem("lockerroom_lang", lang);
    document.documentElement.lang = lang;
    // Keep ?lang= in sync so hreflang URLs (/?lang=de, …) open the right language
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("lang") !== lang) {
        params.set("lang", lang);
        window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
      }
    } catch { /* ignore */ }
  }, [lang]);

  // Translation helper: t("gallery.heading")
  const t = (path) => {
    const keys = path.split(".");
    let value = translations[lang] || translations.en;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) break;
    }
    // Fallback to English if missing
    if (value === undefined) {
      let fallback = translations.en;
      for (const key of keys) fallback = fallback?.[key];
      return fallback ?? path;
    }
    return value;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const setMetaTag = (key, content, attr = "name") => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

// Sets document title + meta tags; re-runs when the values change (e.g. language switch)
export const useSeoMeta = ({ title, description, keywords, noindex = false }) => {
  useEffect(() => {
    if (title) {
      document.title = title;
      setMetaTag("og:title", title, "property");
      setMetaTag("twitter:title", title);
    }
    setMetaTag("description", description);
    setMetaTag("og:description", description, "property");
    setMetaTag("twitter:description", description);
    setMetaTag("keywords", keywords);
    setMetaTag("robots", noindex ? "noindex, nofollow" : "index, follow");
  }, [title, description, keywords, noindex]);
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
